import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { BOARDS_API_BASE_URL } from "../../../constants/configuration";
import {
  createShareLink,
  listShareLinks,
  revokeShareLink,
} from "../api/boardApi";
import { errorToast, successToast } from "../../../redux/actionCreator";

// Per-board share-link manager. Lets a board manager generate read-only
// public links and revoke existing ones. The public viewer URL is
// `<frontend>/share/:token` — the React app's SharedBoard route then fetches
// from `<boards-api>/share/:token` (no auth required).
export default function ShareDialog({ board, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await listShareLinks(board._id);
      setList(res.data || []);
    } catch (e) { errorToast(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [board._id]);

  const buildUrl = (token) => `${window.location.origin}/share/${encodeURIComponent(token)}`;

  const create = async () => {
    setCreating(true);
    try {
      const res = await createShareLink(board._id, { note });
      setList((arr) => [res.data, ...arr]);
      setNote("");
      successToast("Share link created");
    } catch (e) { errorToast(e.message); } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Anyone using it will lose access immediately.")) return;
    try {
      const res = await revokeShareLink(board._id, id);
      setList((arr) => arr.map((x) => (x._id === id ? res.data : x)));
      successToast("Revoked");
    } catch (e) { errorToast(e.message); }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      successToast("Copied to clipboard");
    } catch { /* fall back to alert */ window.prompt("Copy this link:", url); }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Share "{board.name}"</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="muted small">
          Generate a read-only link anyone can use to view this board, even without an account.
          Revoke a link to instantly cut off access.
        </p>

        <div className="share-create-row">
          <Form.Control
            placeholder="Optional note — who is this for?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button variant="primary" disabled={creating} onClick={create}>
            {creating ? "Creating…" : "+ Create link"}
          </Button>
        </div>

        {loading && <div className="muted">Loading…</div>}
        {!loading && list.length === 0 && (
          <div className="empty-state">No share links yet.</div>
        )}
        {!loading && list.map((sl) => {
          const url = buildUrl(sl.token);
          return (
            <div key={sl._id} className={`share-row ${sl.revoked ? "revoked" : ""}`}>
              <div className="share-row-head">
                <strong>{sl.note || "(no note)"}</strong>
                <span className="muted small">
                  created {new Date(sl.createdAt).toLocaleString()}
                  {sl.last_used_at && ` • last used ${new Date(sl.last_used_at).toLocaleString()}`}
                </span>
              </div>
              <div className="share-row-url">
                <code>{url}</code>
              </div>
              <div className="share-row-actions">
                {!sl.revoked && (
                  <>
                    <button className="linkish" onClick={() => copy(url)}>Copy</button>
                    <button className="linkish" onClick={() => window.open(url, "_blank")}>Open</button>
                    <button className="linkish del" onClick={() => revoke(sl._id)}>Revoke</button>
                  </>
                )}
                {sl.revoked && <span className="revoked-tag">revoked</span>}
              </div>
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
